import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// SharkPanzer Guarantee Component
const SharkPanzerGuarantee: React.FC<{ pointCount: number }> = ({ pointCount }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'rgba(0, 153, 255, 0.1)',  // Light blue background
        color: '#0099ff',  // Blue text color
        borderRadius: 2,
        px: 2,
        py: 1.5,
        mb: 3,
        border: '1px solid rgba(0, 153, 255, 0.3)'
      }}
    >
      <VerifiedUserIcon sx={{ mr: 1.5, color: '#0099ff' }} />
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        <strong>Shark Panzer Guarantee:</strong> We guarantee you that if you read all these {pointCount} points, you don't have to watch this entire video. Every detail is accurately covered with 100% factual information!
      </Typography>
    </Box>
  );
};

interface FullCapsuleProps {
  content: string;
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

const FullCapsule: React.FC<FullCapsuleProps> = ({
  content,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  // Count the number of points in the content using regex
  const [pointCount, setPointCount] = useState<number>(30); // Default to 30 if can't detect
  
  useEffect(() => {
    // Count numbered points in the content
    try {
      const pointMatches = content.match(/^\d+\./gm) ||
                          content.match(/\n\d+\./gm) ||
                          content.match(/\n\s*\d+\./gm);
      
      if (pointMatches && pointMatches.length > 0) {
        setPointCount(pointMatches.length);
      } else {
        // Fallback to estimating based on paragraphs
        const paragraphs = content.split('\n\n').filter(p => p.trim().length > 0);
        setPointCount(Math.max(paragraphs.length, 30));
      }
    } catch (error) {
      console.error('Error parsing content:', error);
      setPointCount(30);
    }
  }, [content]);
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle copy
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    onCopy();
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMoreMenuClose();
  };
  
  // Handle download
  const handleDownload = () => {
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and click it
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_capsule.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onDownload();
    handleMoreMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${videoTitle} - Full Capsule</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 20px;
                color: #333;
              }
              h1 {
                font-size: 24px;
                margin-bottom: 20px;
              }
              .time-saved {
                color: #06d6a0;
                font-weight: bold;
                margin-bottom: 20px;
              }
              .content {
                white-space: pre-wrap;
              }
              @media print {
                body {
                  font-size: 12pt;
                }
              }
            </style>
          </head>
          <body>
            <h1>${videoTitle} - Full Capsule</h1>
            <div class="time-saved">Saved ${timeSaved} minutes</div>
            <div class="content">${content.replace(/\n/g, '<br>')}</div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    
    onPrint();
    handleMoreMenuClose();
  };
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Full Capsule
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* SharkPanzer Guarantee */}
      <SharkPanzerGuarantee pointCount={pointCount} />
      
      <Box
        sx={{ 
          typography: 'body1',
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            mt: 3,
            mb: 2,
            fontWeight: 600,
          },
          '& p': {
            mb: 2,
            // Style for paragraphs that might contain numbered points
            '&:has(strong:first-child)': {
              fontWeight: 500,
            }
          },
          // Style for numbered points in paragraphs (e.g., "1. Point text")
          '& p:has(~ p)': {
            '&[id^="point-"]': {
              borderLeft: '2px solid rgba(0, 153, 255, 0.3)',
              pl: 2,
              py: 0.5,
            }
          },
          '& ul, & ol': {
            mb: 2,
            pl: 4,
          },
          '& li': {
            mb: 2,  // More spacing between list items
            pl: 1,  // Padding for better readability
            lineHeight: 1.6,
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            pl: 2,
            py: 1,
            my: 2,
            bgcolor: 'rgba(0, 180, 216, 0.05)',
          },
          '& code': {
            fontFamily: 'monospace',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            p: 0.5,
            borderRadius: 1,
          },
          '& pre': {
            fontFamily: 'monospace',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            mb: 2,
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            mb: 2,
          },
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
          },
          '& th': {
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            fontWeight: 600,
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {content}
        </ReactMarkdown>
      </Box>
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default FullCapsule;
